import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { delay, tap } from 'rxjs/operators';

import { NgbActiveModal, NgbModalRef, NgbDateStruct, NgbTimeStruct, NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { EventManager, AlertService, JhiLanguageService, DateUtils } from 'ng-jhipster';

import { Lesson } from './lesson.model';
import { LessonPopupService } from './lesson-popup.service';
import { LessonService } from './lesson.service';
import { School, SchoolService } from '../school';
import { ClassType, ClassTypeService, ClassTypeTypeaheadService } from '../class-type';
import { Student, StudentService, StudentTypeaheadService, StudentUtilService } from '../student';
import { Location, LocationService } from '../location';
import { AbstractDialogComponent } from '../abstract-dialog-component/abstract-dialog.component';

import { SERVER_DATE_TIME_FORMAT } from '../../shared/constants/date-time.constants';
import { LESSONS_BULK_SAVE_WAIT_TIME_MILLIS } from '../../shared/constants/lesson.constants';
import { Principal, CRUD_ACTIONS, DateUtilities } from '../../shared/index';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { LessonFamily, LessonFamilyService } from '../lesson-family';
import { LessonUtilService } from './lesson.util.service';

@Component({
    selector: 'jhi-lesson-dialog',
    templateUrl: './lesson-dialog.component.html'
})
export class LessonDialogComponent extends AbstractDialogComponent implements OnInit  {

    lesson: Lesson;
    lessonFamily: LessonFamily;
    lessonFamilyMembersCount: number;

    lessonDate: NgbDateStruct;
    originalLessonDate: NgbDateStruct;
    lessonStartTime: NgbTimeStruct;
    lessonEndTime: NgbTimeStruct;

    authorities: any[];
    isSavingSingle: boolean;
    isSavingBulk: boolean;

    classtypes: ClassType[];

    students: Student[];

    locations: Location[];

    teachers: Student[];

    // typeahead for the class type
    classTypeSearch = this.classTypeTypeaheadService.search;

    classTypeResultFormatter = this.classTypeTypeaheadService.resultFormatter;

    classTypeInputFormatter = (classType: any) => this.classTypeTypeaheadService
                                                      .inputFormatter(classType);

    updateAction: string;
    readonly SINGLE_UPDATE_ACTION = 'SINGLE_UPDATE';
    readonly BULK_UPDATE_ACTION = 'BULK_UPDATE';

    constructor(
        public activeModal: NgbActiveModal,
        private jhiLanguageService: JhiLanguageService,
        alertService: AlertService,
        private lessonService: LessonService,
        schoolService: SchoolService,
        private classTypeService: ClassTypeService,
        private studentService: StudentService,
        private studentUtilService: StudentUtilService,
        private locationService: LocationService,
        private eventManager: EventManager,
        private calendar: NgbCalendar,
        private dateUtils: DateUtils,
        private datePipe: DatePipe,
        private classTypeTypeaheadService: ClassTypeTypeaheadService,
        private lessonFamilyService: LessonFamilyService,
        private router: Router,
        principal: Principal
    ) {
        super(schoolService, alertService, principal);
    }

    ngOnInit() {
        this.initSavingState();
        // we force the update action to SINGLE because the lesson does not belong to a family
        // otherwise, we force the user to select an update action to diminish mistakes
        // of updating only this class instead of the series
        if (!this.lesson.lessonFamilyId) {
            this.updateAction = this.SINGLE_UPDATE_ACTION;
        }

        this.authorities = ['ROLE_USER', 'ROLE_ADMIN'];
        this.originalLessonDate = DateUtilities.toDateStruct(this.lesson.begining);
        this.lessonStartTime = DateUtilities.toTimeStruct(this.lesson.begining);
        this.lessonEndTime = DateUtilities.toTimeStruct(this.lesson.ending);

        // Record the original date, this is used to make the date immutable when bulk update is used
        // Because, when a bulk update is used, the date of the lessons are not changed
        this.lessonDate = DateUtilities.toDateStruct(this.lesson.begining);

        if (this.lesson && this.lesson.lessonFamilyId) {
            this.lessonFamilyService
                .find(this.lesson.lessonFamilyId)
                .subscribe( (res: LessonFamily) => {
                    this.lessonFamily = res;
                },
                (error: HttpErrorResponse) => this.onError(error) );

                const futureOpenLessonsQuery: string =
                    LessonUtilService.getNexLessonsOfFamilyQuery(
                        this.lesson.lessonFamilyId
                        , this.lesson.begining
                        , 'OPEN'
                    );

                if (futureOpenLessonsQuery) {
                    this.lessonService
                    .count({ query: futureOpenLessonsQuery})
                    .subscribe( (res: number) => {
                        this.lessonFamilyMembersCount = res;
                    },
                    (error: HttpErrorResponse) => this.onError(error) );
                } else {
                    this.lessonFamilyMembersCount = 0;
                }
        }

        this.loadLocations();
        this.loadTeachersList();
        this.loadSchools();
    }
    clear() {
        this.activeModal.dismiss('cancel');
    }

    save(form: NgForm) {

        // validate the class type text field
        let classType: any = this.lesson.classType;
        let classTypeInput = form !== undefined ? form.controls['classType'] : null;
        let classTypeInputValue = classTypeInput && classTypeInput.value ? classTypeInput.value : null;

        if (!classType && (classTypeInputValue === null || classTypeInputValue.trim().length === 0)) {
            // We nullify the class type cause the input is empty
            this.lesson.classType = null;
        } else if (classTypeInput.dirty && !classType.name)  {
            // We reset the input cause it does not have a class type but a non empty string
            classTypeInput.reset();
            return;
        }

        let begining: Date = new Date();
        begining.setDate(this.lessonDate.day);
        begining.setMonth(this.lessonDate.month - 1);
        begining.setFullYear(this.lessonDate.year);
        begining.setHours(this.lessonStartTime.hour);
        begining.setMinutes(this.lessonStartTime.minute);
        this.lesson.begining = this.datePipe
        .transform(begining, SERVER_DATE_TIME_FORMAT);

        let ending: Date = new Date();
        ending.setDate(this.lessonDate.day);
        ending.setMonth(this.lessonDate.month - 1 );
        ending.setFullYear(this.lessonDate.year);
        ending.setHours(this.lessonEndTime.hour);
        ending.setMinutes(this.lessonEndTime.minute);
        this.lesson.ending = this.datePipe
        .transform(ending, SERVER_DATE_TIME_FORMAT);

        this.lesson.published = this.lessonService.getDefaultValueForPublished(this.lesson);
        if (this.lesson.id !== undefined) {
            // We are updating
            this.isSavingSingle = true;
            this.lesson.begining = this.dateUtils.toDate(this.lesson.begining);
            this.lesson.ending = this.dateUtils.toDate(this.lesson.ending);
            if (this.updateAction === this.SINGLE_UPDATE_ACTION) {
                this.lessonService.update(this.lesson)
                    .subscribe((res: Lesson) =>
                        this.onSaveSuccess(res, CRUD_ACTIONS.UPDATE),
                        (res: HttpErrorResponse) => this.onSaveError(res));
            } else {
                // We are updating several lessons
                this.isSavingBulk = true;
                this.lessonService.bulkUpdate(this.lesson).pipe(
                    delay(LESSONS_BULK_SAVE_WAIT_TIME_MILLIS), // Introduces a delay before processing the result
                    tap((res: Lesson) => {
                        this.onBulkSaveSuccess(res, CRUD_ACTIONS.UPDATE);
                    })
                ).subscribe(
                    (res) => {
                        // Handle success if needed (already in tap)
                    },
                    (error: HttpErrorResponse) => {
                        this.onSaveError(error);
                    }
                );
            }
        } else {
            // We are creating
            this.isSavingSingle = true;
            this.lessonService.create(this.lesson)
                .subscribe((res: Lesson) =>
                    this.onSaveSuccess(res, CRUD_ACTIONS.CREATE),
                    (res: HttpErrorResponse) => this.onSaveError(res));
        }
    }

    startTimeChanged(startTime: NgbTimeStruct) {
        if (!startTime) {
            return;
        }

        let start: Date = new Date();
        start.setHours(startTime.hour);
        start.setMinutes(startTime.minute);
        let end: Date = new Date();
        end.setHours(start.getHours() + 1);
        end.setMinutes(start.getMinutes() + 30);
        this.lessonEndTime = {
            hour : end.getHours(),
            minute : end.getMinutes(),
            second : 0
        };
    }

    private resetLessonDate() {
        this.lessonDate = this.originalLessonDate;
    }

    private onSaveSuccess(result: Lesson, action: String) {
        if (action === CRUD_ACTIONS.CREATE) {
            this.router.navigateByUrl('/lesson/' + result['id'], { replaceUrl: true });
        } else {
            this.eventManager.broadcast({ name: 'lessonListModification', content: {
                action: action,
                content: result
            }});
        }

        this.initSavingState();
        this.activeModal.dismiss(result);
    }

    private onBulkSaveSuccess(result: Lesson, action: String) {
        this.eventManager.broadcast({ name: 'lessonBulkUpdate', content: {
            action: action,
            content: result
        }});

        this.initSavingState();
        this.activeModal.dismiss(result);
    }

    private initSavingState() {
        this.isSavingSingle = false;
        this.isSavingBulk = false;
    }

    private onSaveError(error: HttpErrorResponse) {
        this.initSavingState();
        this.alertService.error(error.error, null, null);
    }

    trackSchoolById(index: number, item: School) {
        return item.id;
    }

    trackClassTypeById(index: number, item: ClassType) {
        return item.id;
    }

    trackStudentById(index: number, item: Student) {
        return item.id;
    }

    trackLocationById(index: number, item: Location) {
        return item.id;
    }

    private loadTeachersList() {
        this.studentService.queryTeachers()
        .subscribe(
            (res: HttpResponse<Student[]>) => {
                const teachersRaw = res.body;
                this.teachers = this.studentUtilService.orderStudentsByName(teachersRaw);
            },
            (res: HttpErrorResponse) => {
                this.onError(res);
            }
        );
    }

    private loadLocations() {
        this.locationService.search({query: 'active:true'})
            .subscribe(
            (res: HttpResponse<Location[]>) => {
                this.locations = res.body;
                if (!this.lesson.id && this.locations.length > 0) {
                    this.lesson.location = this.locations[0];
                }
            }, (res: HttpErrorResponse) => this.onError(res));

        this.schoolService.findCurrent().subscribe((res: School) => {
            this.currentSchool = res;
            if (!this.lesson.id) {
                this.lesson.maxParticipants = this.currentSchool.defaultLessonMaxParticipants;
                this.lesson.reservationCancellationPolicy = this.currentSchool.defaultLessonReservationCancellationPolicy;
                this.lesson.reminderPolicy = this.currentSchool.defaultLessonReminderPolicy;
            }
        }, (error: HttpErrorResponse) => {
            this.onError(error);
        });
    }
}

@Component({
    selector: 'jhi-lesson-popup',
    template: ''
})
export class LessonPopupComponent implements OnInit, OnDestroy {

    modalRef: NgbModalRef;
    routeSub: any;

    constructor(
        private route: ActivatedRoute,
        private lessonPopupService: LessonPopupService
    ) { }

    ngOnInit() {
        this.routeSub = this.route.params.subscribe(params => {
            if (params['id']) {
                this.modalRef = this.lessonPopupService
                    .open(LessonDialogComponent as Component, params['id']);
            } else {
                this.modalRef = this.lessonPopupService
                    .open(LessonDialogComponent as Component);
            }

        });
    }

    ngOnDestroy() {
        this.routeSub.unsubscribe();
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/app/entities/lesson/lesson-dialog.component.ts